import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FileText, Plus, Trash2, Edit2, Code, FileSpreadsheet, Image as ImageIcon, Copy, LayoutGrid, List, Share2, FileSignature, Database, Download, Upload } from 'lucide-react';
import { addDoc, collection, serverTimestamp, deleteDoc, doc, updateDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db, appId } from '../../lib/firebase';
import TemplateEditor from './TemplateEditor';
import FormTemplateEditor from './FormTemplateEditor';
import ImageManager from './ImageManager';
import ShareModal from '../../components/ShareModal';

const stripUndefined = (obj) => {
    if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(stripUndefined).filter(v => v !== undefined);
    return Object.fromEntries(
        Object.entries(obj)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, typeof v === 'object' && v !== null ? stripUndefined(v) : v])
    );
};

const BUILTIN_FORM_TEMPLATES = [
    {
        id: 'builtin-intro-outreach',
        templateId: 'builtin-intro-outreach',
        name: 'Intro outreach',
        description: 'Armellini introduction LTL/Fulfillment outreach letter',
        type: 'HTML',
        templateCategory: 'form',
        content: `<h3>Subject: Armellini Logistics Partnership Proposal - {{CompanyName}}</h3><br/><p>Hi {{ContactPerson}},</p><br/><p>I hope this email finds you well.</p><br/><p>My name is {{Owner}} from Armellini Logistics. I'm reaching out because we'd love to partner with {{CompanyName}} to streamline your temperature-controlled freight requirements.</p><br/><p>We specialize in {{ServiceType}} transport, with a primary focus on the {{Industry}} industry. Given your location in {{Location}}, we can provide highly optimized routing and reliable transit schedules.</p><br/><p>Could we hop on a brief 10-minute call next week to discuss how we can support your shipping volumes?</p><br/><p>Best regards,</p><br/><p><strong>{{Owner}}</strong><br/>Armellini Logistics<br/>{{Email}}</p>`
    },
    {
        id: 'builtin-rate-quotation',
        templateId: 'builtin-rate-quotation',
        name: 'Rate quotation',
        description: 'Rate breakdown and estimated deal value letter',
        type: 'HTML',
        templateCategory: 'form',
        content: `<h3>Subject: Logistics Rate Quote: Armellini Transport - {{CompanyName}}</h3><br/><p>Hi {{ContactPerson}},</p><br/><p>Thank you for your interest in Armellini Logistics.</p><br/><p>Based on our discussions regarding your temperature-controlled shipments, we've prepared a custom pricing quote for your {{ServiceType}} shipments:</p><p><strong>Rate Details:</strong> {{QuoteDetails}}</p><p><strong>Estimated Account Deal Value:</strong> {{DealValue}}</p><br/><p>With our fleet's dedicated floral and refrigerated network, we ensure pristine arrival and on-time reliability.</p><br/><p>If you're ready to proceed or would like to tweak these specs, please let me know. We can arrange your first booking immediately.</p><br/><p>Warm regards,</p><br/><p><strong>{{Owner}}</strong><br/>Armellini Logistics<br/>{{Email}}</p>`
    },
    {
        id: 'builtin-credit-app',
        templateId: 'builtin-credit-app',
        name: 'Credit App referral',
        description: 'Direct link to billing configuration and credit application',
        type: 'HTML',
        templateCategory: 'form',
        content: `<h3>Subject: Action Required: Armellini Logistics Credit Application - {{CompanyName}}</h3><br/><p>Hi {{ContactPerson}},</p><br/><p>To establish your direct billing account and begin dispatching your shipments, we've initiated your credit application process.</p><br/><p>Please complete our secure credit application using the following link:</p><p><a href="{{CreditAppLink}}" target="_blank">{{CreditAppLink}}</a></p><br/><p>Once submitted, our logistics administration team will fast-track your approval so we can schedule your first booking.</p><br/><p>If you have any questions during the application, feel free to contact me directly.</p><br/><p>Best regards,</p><br/><p><strong>{{Owner}}</strong><br/>Armellini Logistics<br/>{{Email}}</p>`
    },
    {
        id: 'builtin-onboarding-complete',
        templateId: 'builtin-onboarding-complete',
        name: 'Onboarding complete',
        description: 'Activate and welcome customer account letter',
        type: 'HTML',
        templateCategory: 'form',
        content: `<h3>Subject: Welcome to Armellini Logistics! Onboarding Approved - {{CompanyName}}</h3><br/><p>Hi {{ContactPerson}},</p><br/><p>Congratulations! Your onboarding with Armellini Logistics is now complete, and your credit account is fully activated.</p><br/><p>We are excited to manage your {{ServiceType}} temperature-controlled shipments. Our dispatch and customer success teams are now configured to monitor your account under our active pipeline.</p><br/><p>Your primary administrator and point of contact for this account is {{Owner}}.</p><br/><p>Thank you for choosing Armellini. We look forward to a long and successful partnership.</p><br/><p>Sincerely,</p><br/><p><strong>{{Owner}}</strong><br/>Armellini Logistics<br/>{{Email}}</p>`
    }
];

interface MaintenanceSectionProps {
    title: string;
    description: string;
}

const MaintenanceSection: React.FC<MaintenanceSectionProps> = ({ title, description }) => (
    <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{title}</h2>
        <p className="text-slate-500 dark:text-slate-400 mb-2">{description}</p>
    </div>
);

interface TemplatesViewProps {
    user?: any;
    templates?: any[];
    savedQueries?: any[];
    showToast?: (msg: string) => void;
    teamUsers?: any[];
}

const TemplatesView: React.FC<TemplatesViewProps> = ({ user, templates: initialTemplates = [], savedQueries = [], showToast, teamUsers = [] }) => {
    const loadSavedTemplates = () => {
        try {
            const local = localStorage.getItem('crm_custom_templates_v1');
            if (local) {
                const parsed = JSON.parse(local);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            }
        } catch (e) {
            console.error("Error reading localStorage templates:", e);
        }
        return initialTemplates || [];
    };

    const [templates, setTemplates] = useState<any[]>(loadSavedTemplates);
    const [isEditingTemplate, setIsEditingTemplate] = useState(false);
    const [activeTemplate, setActiveTemplate] = useState<any>(null);
    const [activeCategory, setActiveCategory] = useState('query'); // 'query' | 'form'
    const [showImageManager, setShowImageManager] = useState(false);
    const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [sharingTemplate, setSharingTemplate] = useState<any>(null);
    const importFileInputRef = useRef<HTMLInputElement>(null);

    // Live sync from Firestore for logged in user
    useEffect(() => {
        if (!user?.uid) return;
        try {
            const colRef = collection(db, 'artifacts', appId, 'users', user.uid, 'templates');
            const unsub = onSnapshot(colRef, (snapshot) => {
                const fetched: any[] = snapshot.docs.map(docSnap => ({
                    id: docSnap.id,
                    ...docSnap.data()
                }));
                if (fetched.length > 0) {
                    setTemplates(prev => {
                        const localOnly = prev.filter(p => !fetched.some(f => f.id === p.id || f.templateId === p.id));
                        const combined = [...fetched, ...localOnly];
                        try {
                            localStorage.setItem('crm_custom_templates_v1', JSON.stringify(combined));
                        } catch (e) {}
                        return combined;
                    });
                }
            }, (err) => {
                console.error("Firestore templates fetch error:", err);
            });
            return () => unsub();
        } catch (e) {
            console.error("Error setting up template listener:", e);
        }
    }, [user?.uid]);

    const sortedTemplates = useMemo(() => {
        const userTemplates = templates || [];
        if (activeCategory === 'form') {
            const userFormTemplates = userTemplates.filter(t => t.templateCategory === 'form');
            const userFormNames = new Set(userFormTemplates.map(t => t.name?.toLowerCase()));
            const missingBuiltins = BUILTIN_FORM_TEMPLATES.filter(b => !userFormNames.has(b.name.toLowerCase()));
            const combined = [...userFormTemplates, ...missingBuiltins];
            return combined.sort((a, b) =>
                (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' })
            );
        }

        const queryTemplates = userTemplates.filter(t => t.templateCategory !== 'form');
        return queryTemplates.sort((a, b) =>
            (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' })
        );
    }, [templates, activeCategory]);

    const handleExportJson = (tmpl) => {
        try {
            const cleanTmpl = {
                id: tmpl.id || tmpl.templateId,
                name: tmpl.name,
                type: tmpl.type || 'HTML',
                templateCategory: tmpl.templateCategory || 'form',
                content: tmpl.content || '',
                sections: tmpl.sections || null
            };
            const blob = new Blob([JSON.stringify(cleanTmpl, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `template_${tmpl.id || tmpl.templateId || 'export'}.json`;
            a.click();
            URL.revokeObjectURL(url);
            if (showToast) showToast(`Template "${tmpl.name}" exported to JSON!`);
        } catch (err) {
            console.error(err);
            if (showToast) showToast('Failed to export template JSON', 'error');
        }
    };

    const handleImportJsonFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            const json = JSON.parse(text);

            if (!json.name) {
                if (showToast) showToast('Invalid template file: Name required', 'error');
                return;
            }

            const importId = json.id || `tmpl-${Date.now()}`;
            const cleanTmpl = {
                id: importId,
                templateId: importId,
                name: json.name,
                type: json.type || 'HTML',
                templateCategory: json.templateCategory || 'form',
                content: json.content || '',
                sections: json.sections || null,
                updatedAt: new Date().toISOString()
            };

            setTemplates(prev => {
                const next = [cleanTmpl, ...prev.filter(t => t.id !== importId)];
                try { localStorage.setItem('crm_custom_templates_v1', JSON.stringify(next)); } catch (e) {}
                return next;
            });

            if (user?.uid) {
                try {
                    const data = stripUndefined({
                        ...cleanTmpl,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp()
                    });
                    await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'templates', importId), data, { merge: true });
                } catch (e) {
                    console.warn("Firestore import sync warning:", e);
                }
            }

            if (showToast) showToast(`Template "${json.name}" imported successfully!`);
        } catch (err) {
            console.error(err);
            if (showToast) showToast('Failed to import template JSON', 'error');
        } finally {
            e.target.value = '';
        }
    };

    const handleShare = (tmpl) => {
        setSharingTemplate(tmpl);
        setIsShareModalOpen(true);
    };

    const handleSaveSharing = async (sharedWith) => {
        if (!sharingTemplate) return;
        if (user?.uid) {
            try {
                const templateRef = doc(db, 'artifacts', appId, 'users', user.uid, 'templates', sharingTemplate.id);
                await updateDoc(templateRef, { sharedWith });
            } catch (error) {
                console.warn(error);
            }
        }
        if (showToast) showToast(`Sharing updated for "${sharingTemplate.name}"`);
    };

    const handleEdit = (tmpl) => {
        setActiveTemplate({ ...tmpl });
        setIsEditingTemplate(true);
    };

    const handleDelete = async (id) => {
        if (!confirm('Are you sure you want to delete this template?')) return;
        
        setTemplates(prev => {
            const next = prev.filter(t => t.id !== id && t.templateId !== id);
            try {
                localStorage.setItem('crm_custom_templates_v1', JSON.stringify(next));
            } catch (e) {}
            return next;
        });

        if (user?.uid) {
            try {
                await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'templates', id));
            } catch (err) {
                console.warn("Firestore delete warning:", err);
            }
        }
        if (showToast) showToast('Template deleted successfully');
    };

    const handleDuplicate = async (tmpl) => {
        const newId = `tmpl-${Date.now()}`;
        let cleanSections = tmpl.sections ? JSON.parse(JSON.stringify(tmpl.sections)) : null;
        if (cleanSections?.body) {
            delete cleanSections.body.selectedColumns;
        }

        const copyTmpl = {
            id: newId,
            templateId: newId,
            name: `${tmpl.name} (Copy)`,
            type: tmpl.type || 'HTML',
            templateCategory: tmpl.templateCategory || 'form',
            content: tmpl.content || '',
            sections: cleanSections,
            queryId: tmpl.queryId || null,
            headerQueryId: tmpl.headerQueryId || null,
            headerQuerySql: tmpl.headerQuerySql || null,
            updatedAt: new Date().toISOString()
        };

        setTemplates(prev => {
            const next = [copyTmpl, ...prev];
            try {
                localStorage.setItem('crm_custom_templates_v1', JSON.stringify(next));
            } catch (e) {}
            return next;
        });

        if (user?.uid) {
            try {
                const docData = stripUndefined({
                    ...copyTmpl,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
                await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'templates', newId), docData);
            } catch (e) {
                console.warn('Firestore duplicate warning:', e);
            }
        }

        if (showToast) showToast(`Template duplicated as "${copyTmpl.name}"`);
    };

    const handleSave = async (templateData) => {
        if (!templateData.name || !templateData.name.trim()) {
            if (showToast) showToast('Template name is required', 'error');
            else alert('Template name is required');
            return;
        }

        const templateId = templateData.id || templateData.templateId || `tmpl-${Date.now()}`;

        let content = templateData.content || '';
        if (!content && templateData.sections) {
            content = [
                templateData.sections?.header?.content,
                templateData.sections?.body?.content,
                templateData.sections?.footer?.content
            ].filter(Boolean).join('\n\n');
        }

        const cleanTemplate = {
            id: templateId,
            templateId: templateId,
            name: templateData.name.trim(),
            type: templateData.type || 'HTML',
            templateCategory: templateData.templateCategory || activeCategory || 'form',
            content: content,
            sections: templateData.sections || null,
            queryId: templateData.queryId || null,
            headerQueryId: templateData.headerQueryId || null,
            headerQuerySql: templateData.headerQuerySql || null,
            updatedAt: new Date().toISOString()
        };

        // 1. Save to LocalStorage & local state
        setTemplates(prev => {
            const exists = prev.some(t => t.id === templateId || t.templateId === templateId);
            let next;
            if (exists) {
                next = prev.map(t => (t.id === templateId || t.templateId === templateId) ? cleanTemplate : t);
            } else {
                next = [cleanTemplate, ...prev];
            }
            try {
                localStorage.setItem('crm_custom_templates_v1', JSON.stringify(next));
            } catch (e) {
                console.warn('LocalStorage save warning:', e);
            }
            return next;
        });

        // 2. Sync to Firestore if authenticated
        if (user?.uid) {
            try {
                const docData = stripUndefined({
                    ...cleanTemplate,
                    updatedAt: serverTimestamp()
                });
                await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'templates', templateId), docData, { merge: true });
            } catch (firestoreErr) {
                console.warn('Firestore template sync warning:', firestoreErr);
            }
        }

        if (showToast) showToast(`Template "${cleanTemplate.name}" saved successfully!`);
        else alert(`Template "${cleanTemplate.name}" saved successfully!`);

        setIsEditingTemplate(false);
        setActiveTemplate(null);
    };

    const openNewEditor = () => {
        if (activeCategory === 'form') {
            setActiveTemplate({ name: '', type: 'HTML', templateCategory: 'form', content: '' });
        } else {
            setActiveTemplate({ name: '', type: 'PDF', templateCategory: 'query', content: '', queryId: '' });
        }
        setIsEditingTemplate(true);
    };

    if (isEditingTemplate && activeTemplate?.templateCategory === 'form') {
        return (
            <FormTemplateEditor
                template={activeTemplate}
                user={user}
                onSave={handleSave}
                onCancel={() => { setIsEditingTemplate(false); setActiveTemplate(null); }}
                showToast={showToast}
            />
        );
    }

    if (isEditingTemplate) {
        return (
            <TemplateEditor
                template={activeTemplate}
                savedQueries={savedQueries}
                user={user}
                onSave={handleSave}
                onCancel={() => { setIsEditingTemplate(false); setActiveTemplate(null); }}
                showToast={showToast}
            />
        );
    }

    return (
        <div className="max-w-6xl mx-auto h-full flex flex-col">
            <div className="flex gap-1 mb-6 border-b border-slate-200 dark:border-slate-700">
                <button
                    onClick={() => setActiveCategory('query')}
                    className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors cursor-pointer ${
                        activeCategory === 'query'
                            ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400'
                            : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                    }`}
                >
                    <Database size={15} />
                    Query Templates
                </button>
                <button
                    onClick={() => setActiveCategory('form')}
                    className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors cursor-pointer ${
                        activeCategory === 'form'
                            ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400'
                            : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                    }`}
                >
                    <FileSignature size={15} />
                    Form Templates
                </button>
            </div>

            <div className="flex justify-between items-start mb-6">
                <div>
                    {activeCategory === 'query'
                        ? <MaintenanceSection title="Query Templates" description="Design layouts for your automated reports, linked to saved queries." />
                        : <MaintenanceSection title="Form Templates" description="Design document templates with variable placeholders for use in document workflows." />
                    }
                </div>
                <div className="flex gap-3 items-center">
                    <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 border border-slate-200 dark:border-slate-700">
                        <button
                            onClick={() => setViewMode('cards')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer ${
                                viewMode === 'cards'
                                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                        >
                            <LayoutGrid size={14} /> Cards
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer ${
                                viewMode === 'list'
                                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                        >
                            <List size={14} /> List
                        </button>
                    </div>
                    {activeCategory === 'query' && (
                        <button
                            onClick={() => setShowImageManager(!showImageManager)}
                            className={`flex items-center gap-2 px-4 py-2 border rounded-md font-medium shadow-sm transition-colors cursor-pointer ${showImageManager
                                ? 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-slate-700 dark:border-slate-500 dark:text-white'
                                : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                }`}
                        >
                            <ImageIcon size={18} /> {showImageManager ? 'Hide Images' : 'Manage Images'}
                        </button>
                    )}
                    <input
                        type="file"
                        ref={importFileInputRef}
                        onChange={handleImportJsonFile}
                        accept=".json"
                        className="hidden"
                    />
                    <button
                        onClick={() => importFileInputRef.current?.click()}
                        className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-md font-medium shadow-xs transition-colors cursor-pointer"
                    >
                        <Upload size={16} /> Import JSON
                    </button>
                    <button
                        onClick={openNewEditor}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium shadow-sm transition-colors cursor-pointer"
                    >
                        <Plus size={18} /> {activeCategory === 'form' ? 'New Form Template' : 'New Template'}
                    </button>
                </div>
            </div>

            {showImageManager && (
                <div className="mb-8 animate-in fade-in duration-300">
                    <ImageManager showToast={showToast} />
                </div>
            )}

            {/* Cards View */}
            {viewMode === 'cards' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sortedTemplates?.map(t => (
                        <div key={t.id} className="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all group relative">
                            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                                <button onClick={() => handleExportJson(t)} title="Export JSON" className="p-2 bg-slate-100 text-slate-600 rounded-md hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 cursor-pointer"><Download size={16} /></button>
                                {!t.isShared && (
                                    <button onClick={() => handleShare(t)} title="Share" className="p-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 dark:bg-blue-900/30 cursor-pointer"><Share2 size={16} /></button>
                                )}
                                <button onClick={() => handleDuplicate(t)} title="Duplicate" className="p-2 bg-emerald-50 text-emerald-600 rounded-md hover:bg-emerald-100 dark:bg-emerald-900/30 cursor-pointer"><Copy size={16} /></button>
                                <button onClick={() => handleEdit(t)} title="Edit" className="p-2 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 dark:bg-indigo-900/30 cursor-pointer"><Code size={16} /></button>
                                <button onClick={() => handleDelete(t.id)} title="Delete" className="p-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 dark:bg-red-900/30 cursor-pointer"><Trash2 size={16} /></button>
                            </div>
                            <div className="w-12 h-12 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center mb-4">
                                {t.type === 'PDF' || t.type === 'HTML' ? <FileText size={24} /> : <FileSpreadsheet size={24} />}
                            </div>
                            <h3 className="font-semibold text-slate-800 dark:text-slate-200">{t.name}</h3>
                            <div className="mt-1.5 flex items-center gap-1.5">
                                <span className="font-mono bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200/60 dark:border-indigo-800/60 px-2 py-0.5 rounded text-[11px] font-bold text-indigo-600 dark:text-indigo-400">
                                    ID: {t.templateId || t.id}
                                </span>
                            </div>
                            <div className="flex justify-between items-center mt-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs uppercase tracking-wider text-slate-500 font-bold">{t.type} Template</span>
                                    {t.isShared && (
                                        <span className="text-[10px] px-2 py-0.5 bg-violet-100 text-violet-600 rounded-full font-bold">Shared</span>
                                    )}
                                </div>
                                <span className="text-xs text-slate-400">
                                    {t.updatedAt?.toDate ? t.updatedAt.toDate().toLocaleDateString() : 'Just now'}
                                </span>
                            </div>
                        </div>
                    ))}

                    {sortedTemplates.length === 0 && (
                        <div className="col-span-full py-12 text-center text-slate-400 dark:text-slate-500 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                            <FileText size={36} className="mx-auto mb-2 opacity-50" />
                            <p className="font-medium text-sm">No {activeCategory} templates found.</p>
                            <p className="text-xs mt-1">Click "New {activeCategory === 'form' ? 'Form Template' : 'Template'}" to create one.</p>
                        </div>
                    )}
                </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
                <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 text-slate-500 uppercase text-[11px] font-bold">
                            <tr>
                                <th className="px-6 py-3">Name</th>
                                <th className="px-6 py-3">Type</th>
                                <th className="px-6 py-3">Category</th>
                                <th className="px-6 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                            {sortedTemplates.map(t => (
                                <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                                    <td className="px-6 py-4 font-semibold text-slate-800 dark:text-slate-200">{t.name}</td>
                                    <td className="px-6 py-4 text-xs font-mono">{t.type}</td>
                                    <td className="px-6 py-4 text-xs uppercase">{t.templateCategory || 'query'}</td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button onClick={() => handleEdit(t)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded cursor-pointer"><Code size={16} /></button>
                                            <button onClick={() => handleDuplicate(t)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded cursor-pointer"><Copy size={16} /></button>
                                            <button onClick={() => handleDelete(t.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded cursor-pointer"><Trash2 size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {sortedTemplates.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-6 py-8 text-center text-slate-400">
                                        No {activeCategory} templates found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => { setIsShareModalOpen(false); setSharingTemplate(null); }}
                onSave={handleSaveSharing}
                teamUsers={teamUsers}
                currentSharedWith={sharingTemplate?.sharedWith || []}
                itemName={sharingTemplate?.name || 'Template'}
            />
        </div>
    );
};

export default TemplatesView;
